import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate, fmtDateTime, downloadBlob } from "../lib/api";
import Topbar from "../components/Topbar";
import StatusBadge from "../components/StatusBadge";
import PizzaSpinner from "../components/PizzaSpinner";
import GoodsReceiptModal from "../components/GoodsReceiptModal";
import POEditItemsModal from "../components/POEditItemsModal";
import { ArrowLeft, CheckCircle2, XCircle, AlertTriangle, Building2, User, Truck, Package, Layers, Ban, FileUp, RotateCcw, ShoppingBag, MapPin, FileText, Clock, Check, Boxes, Pencil, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import InvoiceUploadModal from "../components/InvoiceUploadModal";

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [po, setPo] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [approveConfirm, setApproveConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [openInvoiceUpload, setOpenInvoiceUpload] = useState(false);
  const [annulReceipt, setAnnulReceipt] = useState(false);
  const [annulReceiptReason, setAnnulReceiptReason] = useState("");
  const [qtyReq, setQtyReq] = useState(null);            // solicitud de cambio de cantidad pendiente
  const [qtyRejecting, setQtyRejecting] = useState(false);
  const [qtyRejectReason, setQtyRejectReason] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get(`/purchase-orders/${id}`).then((r) => setPo(r.data));

  const loadQtyReq = () => api.get(`/purchase-orders/${id}/quantity-changes`)
    .then((r) => setQtyReq((r.data || []).find((x) => x.status === "pending") || null))
    .catch(() => setQtyReq(null));

  useEffect(() => { load(); loadQtyReq(); /* eslint-disable-next-line */ }, [id]);

  const decideQty = async (approve) => {
    if (!approve && qtyRejectReason.trim().length < 10) {
      toast.error(t("poQty.rejectReasonError"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/quantity-changes/${qtyReq.id}/decision`,
        approve ? { approve: true } : { approve: false, rejection_reason: qtyRejectReason.trim() });
      toast.success(approve ? t("poQty.approvedToast") : t("poQty.rejectedToast"));
      setQtyRejecting(false);
      setQtyRejectReason("");
      await Promise.all([load(), loadQtyReq()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const cancelPo = async () => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/cancel`, { reason: cancelReason });
      toast.success(t("po.poCancelled"));
      await load();
      setCancelling(false);
      setCancelReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const annulReceiptSubmit = async () => {
    if ((annulReceiptReason || "").trim().length < 10) {
      toast.error(t("po.annulReceiptReasonMin"));
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/goods-receipts/${po.goods_receipt.id}`, {
        data: { reason: annulReceiptReason.trim() },
      });
      toast.success(t("po.receiptAnnulled"));
      await load();
      setAnnulReceipt(false);
      setAnnulReceiptReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${id}/decision`, { action, rejected_reason: reason });
      if (action === "approve" && r.data?.status === "pending_approval") {
        toast.success(`Aprobado. Falta la aprobación de ${r.data?.pending_step?.label || "el siguiente paso"}.`);
      } else {
        toast.success(action === "approve" ? t("po.poApproved") : t("po.poRejected"));
      }
      await load();
      setRejecting(false);
      setApproveConfirm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  // --- VoBo handlers para compras externas ---
  const [extRejecting, setExtRejecting] = useState(false);
  const [extRejectReason, setExtRejectReason] = useState("");
  const [extVoboNotes, setExtVoboNotes] = useState("");

  const approveComprasVoBo = async () => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/external/approve-compras`, { notes: extVoboNotes || null });
      toast.success("VoBo de Compras otorgado");
      await load();
      setExtVoboNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const approveDireccionVoBo = async () => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/external/approve-direccion`, { notes: extVoboNotes || null });
      toast.success("VoBo de Direccion otorgado — compra aprobada");
      await load();
      setExtVoboNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const rejectExternal = async () => {
    if (extRejectReason.trim().length < 10) {
      toast.error("El motivo debe tener al menos 10 caracteres");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/external/reject`, { reason: extRejectReason.trim() });
      toast.success("Compra externa rechazada");
      await load();
      setExtRejecting(false);
      setExtRejectReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  if (!po) return <><Topbar title={t("po.title")} /><PizzaSpinner /></>;

  const isExternal = po.po_type === "external_emergency";
  const ext = po.external_purchase || {};
  // Fix WH81 (2026-10-16): "Ver recibo" era un link directo y el backend pide el
  // token en el header (OAuth2PasswordBearer), así que daba 401. Ahora se descarga
  // con la sesión, igual que los comprobantes de Pagos. También el XML.
  const downloadExternalFile = async (kind) => {
    const MIME_EXT = { "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };
    try {
      const res = await api.get(`/purchase-orders/${po.id}/external/${kind}`, { responseType: "blob" });
      const folio = (po.folio || po.id).replace(/\//g, "-");
      const name = kind === "xml"
        ? `cfdi_${folio}.xml`
        : `recibo_${folio}.${MIME_EXT[ext.receipt_content_type] || MIME_EXT[res.data?.type] || "bin"}`;
      downloadBlob(res.data, name);
    } catch (e) {
      toast.error(kind === "xml" ? "No se pudo descargar el XML" : "No se pudo descargar el recibo");
    }
  };
  const extXmlMismatch = !!ext.xml_storage_path && ext.cfdi_total != null && Math.abs(Number(ext.cfdi_total) - Number(ext.total)) > 0.05;
  const flaggedItems = (po.items || []).filter((it) => it.quantity_flag);
  const showStock = (po.items || []).some(
    (it) => it.stock_on_hand !== null && it.stock_on_hand !== undefined
  );

  return (
    <>
      <Topbar
        title={po.folio}
        subtitle={
          isExternal
            ? `${po.branch?.name || ""} · ${ext.supplier_name_free_text || ""}`
            : `${po.branch?.name || ""} · ${po.supplier?.business_name || ""}`
        }
      />
      <div className="p-6 max-w-5xl space-y-5">
        <button
          onClick={() => navigate(-1)}
          data-testid="po-detail-back-btn"
          className="text-sm font-semibold text-slate-500 hover:text-[#1A1A1A] flex items-center gap-1.5"
        >
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>

        {/* Banner requisición de origen (Solicitudes internas) */}
        {po.origin_requisition_id && (
          <div
            data-testid="po-origin-requisition-banner"
            className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-200 rounded-2xl px-5 py-3.5"
          >
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0" />
              <span className="text-sm font-bold text-indigo-900">Esta OC nace de una requisición aprobada</span>
            </div>
            <button
              data-testid="po-origin-requisition-link"
              onClick={() => navigate(`/ops/solicitudes/${po.origin_requisition_id}`)}
              className="text-xs font-bold text-indigo-700 hover:text-indigo-900 underline"
            >
              Ver requisición de origen
            </button>
          </div>
        )}

        {/* Banner cambio de cantidad pendiente (Pedido 2) */}
        {qtyReq && (
          <div data-testid="po-qty-pending-banner" className="bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4 space-y-3">
            <div className="flex items-start gap-3">
              <Boxes className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-amber-900">
                  {user?.role === "supplier" ? t("poQty.pendingBannerSupplier") : t("poQty.pendingBanner")}
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  {user?.role === "supplier"
                    ? t("poQty.requestedBySupplier", { date: fmtDateTime(qtyReq.requested_at) })
                    : t("poQty.requestedBy", { name: qtyReq.requested_by_name || "", date: fmtDateTime(qtyReq.requested_at) })}
                </div>
                <div className="mt-2 space-y-1">
                  {(qtyReq.items || []).map((it) => (
                    <div key={it.supply_id} className="text-xs text-amber-900 font-mono">
                      {t("poQty.lineChange", { name: it.supply_name || it.supply_id, from: it.previous_quantity, to: it.proposed_quantity })}
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs">
                  <span className="text-amber-900">{t("poQty.currentTotal")}: <b className="font-mono">{fmtMXN(qtyReq.previous_total)}</b></span>
                  <span className="text-amber-900">{t("poQty.proposedTotal")}: <b className="font-mono">{fmtMXN(qtyReq.proposed_total)}</b></span>
                </div>
                <div className="mt-2 text-xs text-amber-900"><b>{t("poQty.reason")}:</b> {qtyReq.reason}</div>
              </div>
            </div>
            {user?.role === "corporate_admin" && (
              <div className="space-y-2">
                {qtyRejecting && (
                  <textarea data-testid="po-qty-reject-reason" rows={2} value={qtyRejectReason}
                    onChange={(e) => setQtyRejectReason(e.target.value)} placeholder={t("poQty.rejectReason")}
                    className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
                )}
                <div className="flex items-center gap-2">
                  <button data-testid="po-qty-approve-btn" disabled={busy} onClick={() => decideQty(true)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {t("poQty.approve")}
                  </button>
                  <button data-testid="po-qty-reject-btn" disabled={busy}
                    onClick={() => (qtyRejecting ? decideQty(false) : setQtyRejecting(true))}
                    className="px-4 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                    <XCircle className="w-3.5 h-3.5" /> {t("poQty.reject")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Aviso corto para el proveedor cuando Compras modificó una OC aprobada */}
        {user?.role === "supplier" && (po.modifications || []).length > 0 && (
          <div data-testid="po-supplier-modified-banner" className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl px-5 py-3.5">
            <Pencil className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              {po.modifications.map((m, i) => (
                <div key={i}>Actualizada el {fmtDateTime(m.at)}: {m.reason}</div>
              ))}
            </div>
          </div>
        )}

        {/* Banner compra externa */}
        {isExternal && (
          <div
            data-testid="ext-purchase-banner"
            className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-2xl px-5 py-3.5"
          >
            <ShoppingBag className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <span className="text-sm font-bold text-amber-900">Compra externa de emergencia</span>
              <span className="text-xs text-amber-700 ml-2">— sin OC formal, requiere VoBo doble</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{t("common.status")}</div>
                <div className="mt-1.5"><StatusBadge status={po.status} customClass="text-sm px-3 py-1" /></div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{t("common.date")}</div>
                <div className="text-sm font-semibold mt-1.5">{fmtDate(po.created_at)}</div>
              </div>
            </div>

            {/* Items: normal PO */}
            {!isExternal && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 pl-0 pr-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableSupply")}</th>
                      {showStock && (
                        <th className="text-right py-2 px-3 w-20 whitespace-nowrap text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.stockTitle")}</th>
                      )}
                      <th className="text-right py-2 px-3 w-24 whitespace-nowrap text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.qtyTitle")}</th>
                      <th className="text-right py-2 px-3 w-28 whitespace-nowrap text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.priceTitle")}</th>
                      <th className="text-right py-2 pl-3 pr-0 w-32 whitespace-nowrap text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.subtotal")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.items.map((it, idx) => {
                      const flag = it.quantity_flag;
                      const veryHigh = flag?.level === "muy_alto";
                      return (
                        <tr
                          key={idx}
                          data-testid={flag ? `po-item-flagged-${idx}` : `po-item-${idx}`}
                          className={`border-b border-slate-100 ${flag ? (veryHigh ? "bg-red-50" : "bg-amber-50") : ""}`}
                        >
                          <td className="py-3 pl-0 pr-3 text-sm font-semibold">
                            {it.supply_name} <span className="text-xs text-slate-400 font-normal">({it.unit})</span>
                            {flag && (
                              <span
                                data-testid={`po-item-flag-chip-${idx}`}
                                className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${veryHigh ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}
                              >
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                                {flag.ratio}× lo habitual
                              </span>
                            )}
                          </td>
                          {showStock && (
                            <td className="py-3 px-3 text-sm text-right text-slate-500 font-mono tabular-nums whitespace-nowrap">
                              {it.stock_on_hand !== null && it.stock_on_hand !== undefined ? it.stock_on_hand.toFixed(1) : "—"}
                            </td>
                          )}
                          <td className="py-3 px-3 text-sm text-right font-mono tabular-nums font-semibold whitespace-nowrap">
                            {it.quantity}
                            {flag && (
                              <span className={`block text-[11px] font-normal ${veryHigh ? "text-red-700" : "text-amber-800"}`}>
                                habitual: {flag.reference}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-sm text-right font-mono tabular-nums whitespace-nowrap">{fmtMXN(it.unit_price)}</td>
                          <td className="py-3 pl-3 pr-0 text-sm text-right font-mono tabular-nums font-bold whitespace-nowrap">{fmtMXN(it.subtotal || it.quantity * it.unit_price)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Detalles compra externa */}
            {isExternal && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Proveedor</div>
                    <div className="font-semibold text-slate-800">{ext.supplier_name_free_text}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                      <MapPin className="w-3 h-3" /> Lugar de compra
                    </div>
                    <div className="font-semibold text-slate-800">{ext.purchase_location}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 sm:col-span-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Motivo de emergencia</div>
                    <div className="text-slate-700">{ext.reason}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                      <Clock className="w-3 h-3" /> Fecha de compra
                    </div>
                    <div className="font-semibold text-slate-800">{fmtDateTime(ext.purchased_at)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                        <FileText className="w-3 h-3" /> Recibo / Factura
                      </div>
                      <div className="text-sm font-semibold text-slate-700">Disponible para descarga</div>
                    </div>
                    <button
                      type="button"
                      data-testid="ext-receipt-download-link"
                      onClick={() => downloadExternalFile("receipt")}
                      className="px-3 py-1.5 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition"
                    >
                      Ver recibo
                    </button>
                  </div>
                  {/* Fix WH81 (2026-10-16): XML del CFDI (opcional) */}
                  <div data-testid="ext-xml-card" className="bg-slate-50 rounded-xl p-3 sm:col-span-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                        <FileText className="w-3 h-3" /> XML (CFDI)
                      </div>
                      {ext.xml_storage_path ? (
                        <>
                          <div className="text-xs font-mono text-slate-700 truncate">UUID {ext.cfdi_uuid}</div>
                          <div className="text-xs text-slate-600 truncate">
                            {ext.cfdi_emisor_nombre || "—"}{ext.cfdi_emisor_rfc ? ` · ${ext.cfdi_emisor_rfc}` : ""} · Total XML <span className="font-mono">{fmtMXN(ext.cfdi_total)}</span>
                          </div>
                          {extXmlMismatch && (
                            <div data-testid="ext-xml-total-mismatch" className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
                              <AlertTriangle className="w-3 h-3" /> No coincide con el total de la compra ({fmtMXN(ext.total)})
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-slate-500">Sin XML</div>
                      )}
                    </div>
                    {ext.xml_storage_path && (
                      <button
                        type="button"
                        data-testid="ext-xml-download-btn"
                        onClick={() => downloadExternalFile("xml")}
                        className="px-3 py-1.5 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition flex-shrink-0"
                      >
                        Descargar XML
                      </button>
                    )}
                  </div>
                </div>

                {/* Items de la compra externa */}
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Descripcion</th>
                      <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Cant.</th>
                      <th className="text-left py-2 pl-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Unidad</th>
                      <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">P. Unit.</th>
                      <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ext.items_free_text || []).map((it, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="py-3 text-sm font-semibold">{it.description}</td>
                        <td className="py-3 text-sm text-right">{it.quantity}</td>
                        <td className="py-3 text-sm pl-3 text-slate-500">{it.unit}</td>
                        <td className="py-3 text-sm text-right font-mono">{fmtMXN(it.unit_price)}</td>
                        <td className="py-3 text-sm text-right font-bold">{fmtMXN(it.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-slate-200 flex justify-end">
              <div className="text-right space-y-1 min-w-[240px]" data-testid="po-totals">
                {po.quantity_amended_at && (
                  <div data-testid="po-qty-amended-badge" className="mb-2 inline-flex flex-col items-end gap-0.5 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-900">
                      <Boxes className="w-3 h-3" /> {t("poQty.amendedBadge")}
                    </span>
                    <span className="text-[11px] text-amber-800">
                      {t("poQty.originalTotal")}: <b className="font-mono">{fmtMXN(po.original_total)}</b>
                    </span>
                    <span className="text-[10px] text-amber-700">
                      {t("poQty.amendedBy", { name: po.quantity_amended_by_name || "", date: fmtDateTime(po.quantity_amended_at) })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-slate-500"><span>{t("common.subtotal")}</span><span className="font-mono">{fmtMXN(po.subtotal)}</span></div>
                <div className="flex justify-between text-sm text-slate-500"><span>{t("common.tax")} {po.iva_rate ?? 16}%</span><span className="font-mono">{fmtMXN(po.tax)}</span></div>
                {(po.isr_withholding_amount > 0 || po.iva_withholding_amount > 0) ? (
                  <>
                    <div className="flex justify-between text-sm text-slate-600 pt-2 border-t border-slate-200 mt-1"><span>{t("po.subtotalPlusTax")}</span><span className="font-mono">{fmtMXN(po.total_before_withholdings ?? (po.subtotal + po.tax))}</span></div>
                    {po.isr_withholding_amount > 0 && (
                      <div data-testid="po-isr-withholding" className="flex justify-between text-sm text-red-600"><span>{t("po.isrWithholding")}</span><span className="font-mono">-{fmtMXN(po.isr_withholding_amount)}</span></div>
                    )}
                    {po.iva_withholding_amount > 0 && (
                      <div data-testid="po-iva-withholding" className="flex justify-between text-sm text-red-600"><span>{t("po.ivaWithholding")}</span><span className="font-mono">-{fmtMXN(po.iva_withholding_amount)}</span></div>
                    )}
                    <div className="flex justify-between text-xl font-display font-extrabold text-[#1A1A1A] pt-2 border-t border-slate-200 mt-2"><span>{t("po.totalToPay")}</span><span className="font-mono">{fmtMXN(po.total)}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between text-xl font-display font-extrabold text-[#1A1A1A] pt-2 border-t border-slate-200 mt-2"><span>{t("common.total")}</span><span className="font-mono">{fmtMXN(po.total)}</span></div>
                )}
              </div>
            </div>

            {po.rejected_reason && (
              <div className="mt-5 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-bold text-red-900">{t("po.rejected")}</div>
                  <div className="text-sm text-red-800 mt-0.5">{po.rejected_reason}</div>
                </div>
              </div>
            )}

            {/* Modificaciones — historial completo para roles internos (Pedido de Gustavo, 27/08) */}
            {user?.role !== "supplier" && (po.modifications || []).length > 0 && (
              <div data-testid="po-modifications-section" className="mt-5 border-t border-slate-200 pt-5 space-y-3">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Modificaciones</div>
                {po.modifications.map((m, i) => (
                  <div key={i} data-testid={`po-modification-${i}`} className="bg-slate-50 rounded-xl p-3.5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-[#1A1A1A]">{m.by_name}</span>
                      <span className="text-xs text-slate-500">{fmtDateTime(m.at)}</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1"><b>Motivo:</b> {m.reason}</div>
                    <div className="mt-2 space-y-1">
                      {(m.changes || []).map((c, ci) => (
                        <div key={ci} className="text-xs font-mono text-slate-700 flex items-center gap-1.5">
                          <span className="font-semibold">{c.supply_name}:</span>
                          {c.removed ? (
                            <span className="text-red-600">se quitó (antes {c.before_qty})</span>
                          ) : c.added ? (
                            <span className="text-emerald-600">se agregó ({c.after_qty})</span>
                          ) : (
                            <span className="flex items-center gap-1 text-amber-700">{c.before_qty} <ArrowRight className="w-3 h-3" /> {c.after_qty}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      Total: {fmtMXN(m.total_before)} → {fmtMXN(m.total_after)}
                      {m.approvals_reset && <span className="ml-2 text-amber-700 font-semibold">· aprobación reiniciada</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {po.status === "cancelled" && (
              <div data-testid="po-cancelled-banner" className="mt-5 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <Ban className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-bold text-red-900">{t("po.cancelled")}</div>
                  <div className="text-sm text-red-800 mt-0.5">{po.cancel_reason}</div>
                  {po.cancelled_by_name && (
                    <div className="text-xs text-red-700 mt-1">{po.cancelled_by_name} · {fmtDateTime(po.cancelled_at)}</div>
                  )}
                </div>
              </div>
            )}
          </motion.div>

          <div className="space-y-4">
            {/* Proveedor: standard PO shows registered supplier; external shows free text */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">{t("common.supplier")}</div>
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4.5 h-4.5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  {isExternal ? (
                    <div className="font-semibold text-sm">{ext.supplier_name_free_text || "—"}</div>
                  ) : (
                    <>
                      <div className="font-semibold text-sm">{po.supplier?.business_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{po.supplier?.rfc}</div>
                      <div className="text-xs text-slate-500 mt-1">{po.supplier?.credit_days} {t("common.creditDays")}</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">{t("common.branch")}</div>
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-4.5 h-4.5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{po.branch?.name}</div>
                  <div className="text-xs text-slate-500">{po.branch?.address}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="po-detail-cost-center">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">{t("po.costCenter")}</div>
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Layers className="w-4.5 h-4.5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{po.cost_center_name || po.cost_center?.name || "—"}</div>
                  <div className="text-xs text-slate-500 font-mono">{po.cost_center_code || "—"}</div>
                  {po.cost_center?.description && (
                    <div className="text-xs text-slate-500 mt-1">{po.cost_center.description}</div>
                  )}
                </div>
              </div>
            </div>

            {/* VoBo timeline — solo para compras externas */}
            {isExternal && (
              <motion.div
                data-testid="ext-vobo-timeline"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4"
              >
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Flujo de aprobacion</div>
                {/* Steps */}
                {[
                  {
                    label: "Cargada",
                    done: true,
                    note: `Por ${po.branch?.name || "sucursal"} el ${fmtDate(po.created_at)}`,
                    testId: "ext-step-created",
                  },
                  {
                    label: "VoBo Compras",
                    done: ext.compras_vobo_status === "approved",
                    rejected: ext.compras_vobo_status === "rejected",
                    note: ext.compras_vobo_status === "approved"
                      ? `Aprobado el ${fmtDate(ext.compras_vobo_at)}`
                      : ext.compras_vobo_status === "rejected"
                        ? `Rechazado: ${ext.compras_vobo_notes || ""}`
                        : "Pendiente",
                    testId: "ext-step-compras",
                  },
                  {
                    label: "VoBo Direccion",
                    done: ext.direccion_vobo_status === "approved",
                    rejected: ext.direccion_vobo_status === "rejected",
                    note: ext.direccion_vobo_status === "approved"
                      ? `Aprobado el ${fmtDate(ext.direccion_vobo_at)}`
                      : ext.direccion_vobo_status === "rejected"
                        ? `Rechazado: ${ext.direccion_vobo_notes || ""}`
                        : "Pendiente",
                    testId: "ext-step-direccion",
                  },
                  {
                    label: "Aprobada",
                    done: po.status === "aprobada",
                    note: po.status === "aprobada" ? `Aprobada el ${fmtDate(po.approved_at)}` : "Pendiente",
                    testId: "ext-step-approved",
                  },
                ].map((step, idx) => (
                  <div key={idx} data-testid={step.testId} className="flex items-start gap-3">
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step.rejected ? "bg-red-100" : step.done ? "bg-emerald-100" : "bg-slate-100"
                    }`}>
                      {step.rejected
                        ? <XCircle className="w-3.5 h-3.5 text-red-500" />
                        : step.done
                          ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                          : <Clock className="w-3.5 h-3.5 text-slate-400" />
                      }
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{step.label}</div>
                      <div className="text-xs text-slate-500">{step.note}</div>
                    </div>
                  </div>
                ))}

                {/* Accion: VoBo Compras */}
                {user?.role === "corporate_admin" && po.status === "pendiente_vobo_compras" && !extRejecting && (
                  <div className="pt-3 border-t border-slate-200 space-y-2">
                    <textarea
                      data-testid="ext-compras-notes"
                      value={extVoboNotes}
                      onChange={(e) => setExtVoboNotes(e.target.value)}
                      placeholder="Notas opcionales..."
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                    />
                    <button
                      data-testid="ext-approve-compras-btn"
                      onClick={approveComprasVoBo}
                      disabled={busy}
                      className="w-full py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Dar VoBo de Compras
                    </button>
                    <button
                      data-testid="ext-reject-compras-btn"
                      onClick={() => setExtRejecting(true)}
                      className="w-full py-2.5 bg-white border border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-50 flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" /> Rechazar
                    </button>
                  </div>
                )}

                {/* Accion: VoBo Direccion */}
                {user?.role === "direccion" && po.status === "pendiente_vobo_direccion" && !extRejecting && (
                  <div className="pt-3 border-t border-slate-200 space-y-2">
                    <textarea
                      data-testid="ext-direccion-notes"
                      value={extVoboNotes}
                      onChange={(e) => setExtVoboNotes(e.target.value)}
                      placeholder="Notas opcionales..."
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                    />
                    <button
                      data-testid="ext-approve-direccion-btn"
                      onClick={approveDireccionVoBo}
                      disabled={busy}
                      className="w-full py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Dar VoBo de Direccion
                    </button>
                    <button
                      data-testid="ext-reject-direccion-btn"
                      onClick={() => setExtRejecting(true)}
                      className="w-full py-2.5 bg-white border border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-50 flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" /> Rechazar
                    </button>
                  </div>
                )}

                {/* Formulario de rechazo */}
                {extRejecting && (
                  <div className="pt-3 border-t border-slate-200 space-y-2">
                    <textarea
                      data-testid="ext-reject-reason-input"
                      value={extRejectReason}
                      onChange={(e) => setExtRejectReason(e.target.value)}
                      placeholder="Motivo del rechazo (minimo 10 caracteres)..."
                      rows={3}
                      className="w-full px-3 py-2 border border-red-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-red-400"
                    />
                    <button
                      data-testid="ext-confirm-reject-btn"
                      onClick={rejectExternal}
                      disabled={busy || extRejectReason.trim().length < 10}
                      className="w-full py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 disabled:opacity-50"
                    >
                      Confirmar rechazo
                    </button>
                    <button
                      onClick={() => setExtRejecting(false)}
                      className="w-full py-2 text-sm text-slate-500 hover:text-[#1A1A1A]"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {po.can_approve && !isExternal && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-[#FFFBE6] to-white rounded-2xl border border-[#FFDF00] p-5" data-testid="po-decision-card">
                <div className="text-xs uppercase tracking-wider font-bold text-[#1A1A1A] mb-3">
                  {t("po.decision")}
                  {po.pending_step?.total > 1 && (
                    <span className="ml-2 font-semibold text-slate-500 normal-case tracking-normal">
                      paso {(po.pending_step.index ?? 0) + 1} de {po.pending_step.total} · {po.approver_label}
                    </span>
                  )}
                </div>
                {rejecting ? (
                  <div className="space-y-2">
                    <textarea
                      data-testid="po-reject-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t("po.rejectReasonPh")}
                      rows={3}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                    <button
                      data-testid="po-confirm-reject-btn"
                      disabled={busy || !reason}
                      onClick={() => decide("reject")}
                      className="w-full py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 disabled:opacity-50"
                    >
                      {t("po.confirmReject")}
                    </button>
                    <button
                      onClick={() => setRejecting(false)}
                      className="w-full py-2 text-sm text-slate-500 hover:text-[#1A1A1A]"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                ) : approveConfirm ? (
                  <div className="space-y-3" data-testid="po-approve-confirm-step">
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-red-800">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {flaggedItems.length === 1
                          ? "1 línea con cantidad fuera de lo normal"
                          : `${flaggedItems.length} líneas con cantidad fuera de lo normal`}
                      </div>
                      <ul className="mt-2 space-y-1.5">
                        {flaggedItems.map((it, i) => (
                          <li key={i} data-testid={`po-approve-flag-item-${i}`} className="text-xs text-red-900">
                            <b>{it.supply_name}</b>: pide <b>{it.quantity} {it.unit}</b> · habitual: <b>{it.quantity_flag.reference}</b>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      data-testid="po-approve-confirm-btn"
                      disabled={busy}
                      onClick={() => decide("approve")}
                      className="w-full py-3 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Lo revisé, aprobar igual
                    </button>
                    <button
                      onClick={() => setApproveConfirm(false)}
                      className="w-full py-2 text-sm text-slate-500 hover:text-[#1A1A1A]"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {flaggedItems.length > 0 && (
                      <div data-testid="po-approve-flag-warning" className="flex items-start gap-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-1">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                          Hay {flaggedItems.length === 1 ? "una cantidad" : `${flaggedItems.length} cantidades`} muy
                          por encima de lo habitual. Revísalas antes de aprobar.
                        </span>
                      </div>
                    )}
                    <button
                      data-testid="po-approve-btn"
                      disabled={busy}
                      onClick={() => (flaggedItems.length > 0 ? setApproveConfirm(true) : decide("approve"))}
                      className="w-full py-3 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> {t("common.approve")}
                    </button>
                    <button
                      data-testid="po-reject-btn"
                      onClick={() => setRejecting(true)}
                      className="w-full py-3 bg-white border border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-50 flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" /> {t("common.reject")}
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {po.status === "pending_approval" && !isExternal && !po.can_approve && (
              <div data-testid="po-waiting-approval-card" className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-600">
                    Esperando la aprobación de <b>{po.approver_label || "Compras"}</b>
                    {po.pending_step?.total > 1 && ` (paso ${(po.pending_step.index ?? 0) + 1} de ${po.pending_step.total})`}.
                    <div className="text-xs text-slate-500 mt-1">El proveedor no recibe esta orden hasta que se apruebe.</div>
                  </div>
                </div>
              </div>
            )}

            {(po.approval_steps || []).length > 1 && (
              <div data-testid="po-approval-steps-card" className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Flujo de aprobación</div>
                {po.approval_steps.map((s, i) => (
                  <div key={i} data-testid={`po-approval-step-${i}`} className="flex items-start gap-3">
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      s.status === "aprobada" ? "bg-emerald-100" : s.status === "rechazada" ? "bg-red-100" : "bg-slate-100"
                    }`}>
                      {s.status === "aprobada" ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                        : s.status === "rechazada" ? <XCircle className="w-3.5 h-3.5 text-red-500" />
                        : <Clock className="w-3.5 h-3.5 text-slate-400" />}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{s.label}</div>
                      <div className="text-xs text-slate-500">
                        {s.status === "aprobada" ? `Aprobó ${s.decided_by_name || ""} · ${fmtDateTime(s.decided_at)}`
                          : s.status === "rechazada" ? `Rechazó ${s.decided_by_name || ""}`
                          : "Pendiente"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {user?.role === "supplier" && ["approved", "received"].includes(po.status) && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-[#FFFBE6] to-white rounded-2xl border border-[#FFDF00] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <FileUp className="w-4 h-4 text-[#1A1A1A]" />
                  <div className="text-xs uppercase tracking-wider font-bold text-[#1A1A1A]">{t("po.invoicePending")}</div>
                </div>
                <p className="text-xs text-slate-600 mb-3 leading-relaxed">{t("po.invoicePendingHint")}</p>
                <button
                  data-testid="po-upload-invoice-btn"
                  onClick={() => setOpenInvoiceUpload(true)}
                  className="btn-primary-chizy w-full py-2.5 rounded-xl text-sm flex items-center justify-center gap-2"
                >
                  <FileUp className="w-4 h-4" /> {t("po.uploadInvoice")}
                </button>
              </motion.div>
            )}

            {po.goods_receipt && !po.goods_receipt.deleted_at && (
              user?.role === "corporate_admin" ||
              (user?.role === "branch_manager" && po.goods_receipt.branch_id === user.branch_id)
            ) && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-orange-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <RotateCcw className="w-4 h-4 text-orange-600" />
                  <div className="text-xs uppercase tracking-wider font-bold text-orange-700">{t("po.annulReceiptTitle")}</div>
                </div>
                {!annulReceipt ? (
                  <>
                    <p className="text-xs text-slate-600 mb-3 leading-relaxed">{t("po.annulReceiptHint")}</p>
                    <button
                      data-testid="po-annul-receipt-btn"
                      onClick={() => setAnnulReceipt(true)}
                      className="w-full py-2.5 bg-white border border-orange-300 text-orange-700 rounded-xl text-sm font-bold hover:bg-orange-50 flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" /> {t("po.annulReceiptAction")}
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      data-testid="po-annul-receipt-reason"
                      value={annulReceiptReason}
                      onChange={(e) => setAnnulReceiptReason(e.target.value)}
                      placeholder={t("po.annulReceiptReasonPh")}
                      rows={3}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                    />
                    <button
                      data-testid="po-confirm-annul-receipt-btn"
                      disabled={busy || annulReceiptReason.trim().length < 10}
                      onClick={annulReceiptSubmit}
                      className="w-full py-2.5 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 disabled:opacity-50"
                    >
                      {t("po.confirmAnnulReceipt")}
                    </button>
                    <button
                      onClick={() => { setAnnulReceipt(false); setAnnulReceiptReason(""); }}
                      className="w-full py-2 text-sm text-slate-500 hover:text-[#1A1A1A]"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Fix WH12 (2026-08-30): sólo la sucursal confirma la recepción (pedido de Claudia, video 25/08) */}
            {user?.role === "branch_manager" && po.status === "approved" && !po.goods_receipt && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-[#FFFBE6] to-white rounded-2xl border border-[#FFDF00] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-4 h-4 text-[#1A1A1A]" />
                  <div className="text-xs uppercase tracking-wider font-bold text-[#1A1A1A]">{t("po.receiptPending")}</div>
                </div>
                <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                  {t("po.receiptPendingHint")}
                </p>
                <button
                  data-testid="po-confirm-receipt-btn"
                  onClick={() => setOpenReceipt(true)}
                  className="btn-primary-chizy w-full py-2.5 rounded-xl text-sm flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> {t("po.confirmReceipt")}
                </button>
              </motion.div>
            )}

            {po.can_edit && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Pencil className="w-4 h-4 text-[#1A1A1A]" />
                  <div className="text-xs uppercase tracking-wider font-bold text-[#1A1A1A]">Modificar orden</div>
                </div>
                <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                  {po.status === "approved"
                    ? "¿El proveedor avisó que no tiene algún producto? Puedes bajar cantidades o quitar líneas — nunca agregar ni subir."
                    : "Agrega, quita o cambia cantidades antes de que se apruebe."}
                </p>
                <button
                  data-testid="po-edit-btn"
                  onClick={() => setOpenEdit(true)}
                  className="w-full py-2.5 bg-white border border-slate-300 text-[#1A1A1A] rounded-xl text-sm font-bold hover:bg-slate-50 flex items-center justify-center gap-2"
                >
                  <Pencil className="w-4 h-4" /> Modificar
                </button>
              </motion.div>
            )}

            {po.can_cancel && !po.goods_receipt && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-red-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Ban className="w-4 h-4 text-red-600" />
                  <div className="text-xs uppercase tracking-wider font-bold text-red-700">{t("po.cancelTitle")}</div>
                </div>
                {!cancelling ? (
                  <>
                    <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                      {po.status === "pending_approval" && user?.role !== "corporate_admin"
                        ? "Todavía no está aprobada: si te equivocaste en algo, puedes cancelarla acá mismo indicando el motivo. Compras queda avisado."
                        : t("po.cancelHint")}
                    </p>
                    <button
                      data-testid="po-cancel-btn"
                      onClick={() => setCancelling(true)}
                      className="w-full py-2.5 bg-white border border-red-200 text-red-600 rounded-xl text-sm font-bold hover:bg-red-50 flex items-center justify-center gap-2"
                    >
                      <Ban className="w-4 h-4" /> {t("po.cancelPo")}
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      data-testid="po-cancel-reason"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder={t("po.cancelReasonPh")}
                      rows={3}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                    <button
                      data-testid="po-confirm-cancel-btn"
                      disabled={busy || cancelReason.trim().length < 5}
                      onClick={cancelPo}
                      className="w-full py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 disabled:opacity-50"
                    >
                      {t("po.confirmCancel")}
                    </button>
                    <button
                      onClick={() => { setCancelling(false); setCancelReason(""); }}
                      className="w-full py-2 text-sm text-slate-500 hover:text-[#1A1A1A]"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {po.goods_receipt && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2.5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${po.goods_receipt.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-bold text-base text-[#1A1A1A]">{t("po.receiptCard")}</div>
                  <div className="text-xs text-slate-500">{t("po.receivedOn", { date: fmtDateTime(po.goods_receipt.received_at) })}</div>
                </div>
              </div>
              {po.goods_receipt.status === "discrepancy" ? (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold"><AlertTriangle className="w-3.5 h-3.5" /> {t("po.diffPositive")}</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> {t("po.matchesPo")}</span>
              )}
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableSupply")}</th>
                  <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableOrdered")}</th>
                  <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableReceived")}</th>
                  <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableDifference")}</th>
                  {/* Fix WH16 (2026-08-31): las tres columnas en pesos. */}
                  <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableAmountOrdered")}</th>
                  <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableAmountReceived")}</th>
                  <th className="text-right py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableAmountDifference")}</th>
                </tr>
              </thead>
              <tbody>
                {po.goods_receipt.items.map((it, idx) => {
                  const hasDiff = Math.abs(it.discrepancy) > 0.001;
                  // Recepciones anteriores a WH16 no traen montos: se calculan
                  // aca con el precio de la OC para no dejar la columna vacia.
                  const poItem = (po.items || []).find((p) => p.supply_id === it.supply_id) || {};
                  const price = it.unit_price ?? poItem.unit_price ?? 0;
                  const amtOrd = it.amount_ordered ?? it.quantity_ordered * price;
                  const amtRec = it.amount_received ?? it.quantity_received * price;
                  const amtDiff = it.amount_discrepancy ?? amtRec - amtOrd;
                  return (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-3 text-sm font-semibold">{it.supply_name} <span className="text-xs text-slate-400 font-normal">({it.unit})</span></td>
                      <td className="py-3 text-sm text-right font-mono">{it.quantity_ordered}</td>
                      <td className="py-3 text-sm text-right font-mono font-bold">{it.quantity_received}</td>
                      <td className={`py-3 text-sm text-right font-mono font-bold ${hasDiff ? (it.discrepancy > 0 ? "text-amber-700" : "text-red-700") : "text-emerald-700"}`}>
                        {it.discrepancy > 0 ? "+" : ""}{it.discrepancy}
                      </td>
                      <td className="py-3 text-sm text-right font-mono text-slate-500">{fmtMXN(amtOrd)}</td>
                      <td className="py-3 text-sm text-right font-mono">{fmtMXN(amtRec)}</td>
                      <td className={`py-3 text-sm text-right font-mono font-bold ${Math.abs(amtDiff) > 0.01 ? (amtDiff > 0 ? "text-amber-700" : "text-red-700") : "text-emerald-700"}`}>
                        {amtDiff > 0 ? "+" : ""}{fmtMXN(amtDiff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {po.goods_receipt.notes && (
              <div className="mt-4 bg-slate-50 rounded-xl p-3 text-sm text-slate-700">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-0.5">{t("common.notes")}</span>
                {po.goods_receipt.notes}
              </div>
            )}
          </motion.div>
        )}

        {/* Fix WH16 (2026-08-31): OC vs Factura lado a lado. Antes habia que
            abrir dos pantallas y restar a mano para saber si la diferencia era
            de cantidad o de precio (video de Claudia 25/08, min 5:25). */}
        {po.invoice && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="po-vs-invoice-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2.5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${po.invoice.price_mismatch ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-bold text-base text-[#1A1A1A]">{t("po.invoiceVsPo")}</div>
                  <div className="text-xs text-slate-500">{po.invoice.folio_fiscal || po.invoice.uuid_cfdi} · {po.invoice.metodo_pago || "—"}</div>
                </div>
              </div>
              {po.invoice.price_mismatch ? (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-100 text-red-800 text-xs font-bold"><AlertTriangle className="w-3.5 h-3.5" /> {t("po.invoiceDiff")}</span>
              ) : po.invoice.tax_mismatch ? (
                /* Fix WH68 (2026-10-09): el subtotal coincide; solo cambia IVA o descuento. */
                <span data-testid="po-invoice-tax-badge" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-200 text-slate-700 text-xs font-bold">{t("invoices.taxBadge")}</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> {t("po.matchesPo")}</span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("po.amountPo")}</div>
                <div className="text-base font-bold font-mono">{fmtMXN(po.invoice.po_total ?? po.total)}</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("po.amountReceived")}</div>
                <div className="text-base font-bold font-mono">{po.goods_receipt ? fmtMXN(po.goods_receipt.amount_received ?? 0) : "—"}</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("po.amountInvoiced")}</div>
                <div className="text-base font-bold font-mono">{fmtMXN(po.invoice.total)}</div>
              </div>
              <div className={`rounded-xl p-3 ${po.invoice.price_mismatch ? "bg-red-50" : po.invoice.tax_mismatch ? "bg-slate-100" : "bg-emerald-50"}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("po.tableAmountDifference")}</div>
                <div className={`text-base font-bold font-mono ${po.invoice.price_mismatch ? "text-red-700" : po.invoice.tax_mismatch ? "text-slate-700" : "text-emerald-700"}`}>
                  {(po.invoice.total_difference ?? 0) > 0 ? "+" : ""}{fmtMXN(po.invoice.total_difference ?? 0)}
                  <span className="text-xs font-normal ml-1">({(po.invoice.difference_percent ?? 0).toFixed(2)}%)</span>
                </div>
              </div>
            </div>
            {po.invoice.po_subtotal != null && (po.invoice.price_mismatch || po.invoice.tax_mismatch) && (
              <p data-testid="po-invoice-subtotal-line" className={`text-xs font-semibold mt-3 ${po.invoice.price_mismatch ? "text-red-700" : "text-slate-600"}`}>
                {po.invoice.price_mismatch
                  ? t("po.subtotalDiffLine", { po: fmtMXN(po.invoice.po_subtotal), inv: fmtMXN(po.invoice.subtotal), diff: `${po.invoice.subtotal_difference > 0 ? "+" : ""}${fmtMXN(po.invoice.subtotal_difference)}` })
                  : t("invoices.taxBadgeHint")}
              </p>
            )}
            <p className="text-xs text-slate-500 mt-3">{t("po.invoiceVsPoHint")}</p>
          </motion.div>
        )}
      </div>

      {openReceipt && (
        <GoodsReceiptModal poId={po.id} onClose={() => setOpenReceipt(false)} onSuccess={load} />
      )}

      {openInvoiceUpload && (
        <InvoiceUploadModal
          po={po}
          onClose={() => setOpenInvoiceUpload(false)}
          onSuccess={() => { setOpenInvoiceUpload(false); load(); }}
        />
      )}

      {openEdit && (
        <POEditItemsModal po={po} onClose={() => setOpenEdit(false)} onSuccess={load} />
      )}
    </>
  );
}
